import React, { useState, useMemo } from 'react';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import { generateTableAnswerLogicDescription, generateTableComparisonRules } from './tableUtils';

/**
 * TableAnswerLogicSummary - Displays answer validation logic for table questions
 * Shows how student answers are evaluated including tolerance, matching rules, and cell types
 *
 * @param {Object} props
 * @param {Object} props.config - The table answer_config object with columns, rows, grading settings
 * @param {boolean} props.compact - If true, shows a more compact view (default: false)
 * @param {Object} props.colors - Theme colors object (optional)
 * @param {string} props.mode - Display mode: "preview" (general rules only) or "full" (all details) (default: "full")
 * @param {string} props.title - Custom title for the accordion header (optional)
 */
const TableAnswerLogicSummary = ({
  config,
  compact = false,
  colors,
  mode = 'full',
  title,
}) => {
  const [expanded, setExpanded] = useState(!compact);

  // Determine if we're in preview mode
  const isPreviewMode = mode === 'preview';

  // Generate the logic description for full mode
  const logicDescription = useMemo(
    () => (isPreviewMode ? null : generateTableAnswerLogicDescription(config)),
    [config, isPreviewMode]
  );

  // Generate general comparison rules for preview mode
  const comparisonRules = useMemo(
    () => generateTableComparisonRules(config),
    [config]
  );

  // Don't render if no config
  if (!config) {
    return null;
  }

  // Get grading settings
  const grading = config.grading || {};
  const gradingMode = grading.mode || 'exact';
  const tolerance = grading.tolerance || 0.01;
  const rowOrderFlexible = grading.rowOrderFlexible || false;
  const caseSensitive = grading.caseSensitive || false;

  // Determine the header title
  const headerTitle = title || (isPreviewMode ? 'How will my answer be graded?' : 'Answer Validation Logic');
  const HeaderIcon = isPreviewMode ? HelpOutlineIcon : InfoOutlinedIcon;

  const baseStyles = {
    container: {
      mt: 2,
      borderRadius: 1,
      border: colors ? `1px solid ${colors.grey?.[700] || '#555'}` : '1px solid #555',
      backgroundColor: colors?.primary?.[800] || 'rgba(0,0,0,0.1)',
    },
    header: {
      color: colors?.blueAccent?.[300] || '#64b5f6',
    },
    label: {
      color: colors?.grey?.[300] || '#bbb',
    },
    chip: {
      backgroundColor: colors?.blueAccent?.[900] || 'rgba(100,181,246,0.1)',
      color: colors?.grey?.[200] || '#ddd',
      fontSize: '0.7rem',
    },
    infoChip: {
      backgroundColor: colors?.greenAccent?.[900] || 'rgba(76,175,80,0.1)',
      color: colors?.grey?.[200] || '#ddd',
      fontSize: '0.7rem',
    },
  };

  return (
    <Box sx={baseStyles.container}>
      <Accordion
        expanded={expanded}
        onChange={() => setExpanded(!expanded)}
        sx={{
          backgroundColor: 'transparent',
          boxShadow: 'none',
          '&:before': { display: 'none' },
        }}
      >
        <AccordionSummary
          expandIcon={<ExpandMoreIcon sx={{ color: baseStyles.header.color }} />}
          sx={{ minHeight: compact ? 40 : 48, px: 2 }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <HeaderIcon sx={{ fontSize: 18, color: baseStyles.header.color }} />
            <Typography variant="subtitle2" fontWeight="bold" sx={baseStyles.header}>
              {headerTitle}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails sx={{ px: 2, pt: 0, pb: 2 }}>
          {/* Comparison Settings */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" sx={{ ...baseStyles.label, display: 'block', mb: 0.5 }}>
              Comparison Settings:
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
              <Chip
                label={gradingMode === 'tolerance' ? `Grading: Tolerance (${tolerance})` : 'Grading: Exact Match'}
                size="small"
                sx={baseStyles.chip}
              />
              <Chip
                label={rowOrderFlexible ? 'Row Order: Flexible' : 'Row Order: Fixed'}
                size="small"
                sx={baseStyles.chip}
              />
              {!isPreviewMode && (
                <Chip
                  label={caseSensitive ? 'Case: Sensitive' : 'Case: Insensitive'}
                  size="small"
                  sx={baseStyles.chip}
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2, borderColor: colors?.grey?.[700] || '#555' }} />

          {/* Preview Mode: General Rules */}
          {isPreviewMode && comparisonRules.length > 0 && (
            <Box>
              <Typography
                variant="caption"
                fontWeight="bold"
                sx={{ ...baseStyles.label, display: 'block', mb: 1 }}
              >
                General Rules:
              </Typography>
              <Box sx={{ ml: 1 }}>
                {comparisonRules.map((rule, idx) => (
                  <Typography
                    key={idx}
                    variant="caption"
                    sx={{ color: colors?.grey?.[400] || '#999', display: 'block' }}
                  >
                    {rule}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}

          {/* Full Mode: Table Structure Info */}
          {!isPreviewMode && logicDescription && (
            <>
              <Box sx={{ mb: 2 }}>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ ...baseStyles.label, display: 'block', mb: 1 }}
                >
                  Table Structure:
                </Typography>
                <Box sx={{ ml: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors?.grey?.[400] || '#999', display: 'block' }}
                  >
                    {logicDescription.columnCount} column{logicDescription.columnCount !== 1 ? 's' : ''} ({logicDescription.columnHeaders.join(', ')})
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors?.grey?.[400] || '#999', display: 'block' }}
                  >
                    {logicDescription.rowCount} row{logicDescription.rowCount !== 1 ? 's' : ''} total
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors?.grey?.[400] || '#999', display: 'block' }}
                  >
                    {logicDescription.prefilledCount} prefilled cell{logicDescription.prefilledCount !== 1 ? 's' : ''} (not graded)
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: colors?.grey?.[400] || '#999', display: 'block' }}
                  >
                    {logicDescription.editableCount} editable cell{logicDescription.editableCount !== 1 ? 's' : ''} (graded)
                  </Typography>
                </Box>
              </Box>

              {/* Column Types */}
              {logicDescription.columnTypes && logicDescription.columnTypes.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="caption"
                    fontWeight="bold"
                    sx={{ ...baseStyles.label, display: 'block', mb: 0.5 }}
                  >
                    Column Types:
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, ml: 1 }}>
                    {logicDescription.columnTypes.map((col, idx) => (
                      <Chip
                        key={idx}
                        label={`${col.header}: ${col.type}`}
                        size="small"
                        sx={baseStyles.infoChip}
                      />
                    ))}
                  </Box>
                </Box>
              )}

              <Divider sx={{ mb: 2, borderColor: colors?.grey?.[700] || '#555' }} />

              {/* Math Comparison Info */}
              <Box>
                <Typography
                  variant="caption"
                  fontWeight="bold"
                  sx={{ ...baseStyles.label, display: 'block', mb: 1 }}
                >
                  Math Comparison:
                </Typography>
                <Box sx={{ ml: 1 }}>
                  <Typography
                    variant="caption"
                    sx={{ color: colors?.grey?.[400] || '#999', display: 'block' }}
                  >
                    Values parsed as numbers when possible
                  </Typography>
                  {gradingMode === 'tolerance' && (
                    <>
                      <Typography
                        variant="caption"
                        sx={{ color: colors?.grey?.[400] || '#999', display: 'block' }}
                      >
                        1/3 matches 0.333... within tolerance
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: colors?.grey?.[400] || '#999', display: 'block' }}
                      >
                        \u221A2 matches 1.414... within tolerance
                      </Typography>
                    </>
                  )}
                </Box>
              </Box>
            </>
          )}
        </AccordionDetails>
      </Accordion>
    </Box>
  );
};

export default TableAnswerLogicSummary;
